import React, { useState } from 'react';
import axios from 'axios';
import BookingModal from '../components/BookingModal';

const EMAIL = 'casey.shore.92@gmail.com';
const LINKEDIN = 'https://www.linkedin.com/in/casey-shore-3a250a163';
const GITHUB = 'https://github.com/BowArrow';

const projectTypes = [
    ['collaboration', 'Collaboration', 'Working together on an existing project'],
    ['consulting', 'Consulting', 'Technical guidance and expertise'],
    ['new-project', 'New project', 'Building something from scratch'],
];

const budgets = [
    ['', 'Select budget range'],
    ['5k-15k', '$5,000 - $15,000'],
    ['15k-50k', '$15,000 - $50,000'],
    ['50k-100k', '$50,000 - $100,000'],
    ['100k+', '$100,000+'],
    ['discuss', 'Let’s discuss'],
];

const timelines = [
    ['', 'Select timeline'],
    ['asap', 'ASAP'],
    ['1-3months', '1-3 months'],
    ['3-6months', '3-6 months'],
    ['6months+', '6+ months'],
    ['ongoing', 'Ongoing partnership'],
];

const emptyForm = {
    name: '',
    email: '',
    company: '',
    role: '',
    budget: '',
    timeline: '',
    message: '',
};

const Contact = () => {

    const [form, setForm] = useState(emptyForm);
    const [projectType, setProjectType] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [error, setError] = useState(null);
    const [isBookingOpen, setIsBookingOpen] = useState(false);

    const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSending(true);
        setError(null);
        setIsSent(false);

        try {
            await axios.post(
                'https://us-central1-casey-portfolio-ai-1992.cloudfunctions.net/sendMail',
                { ...form, projectType }
            );
            setIsSent(true);
            setForm(emptyForm);
            setProjectType(null);
        } catch (err) {
            setError('Something went wrong. Please try again, or email me directly.');
        } finally {
            setIsSending(false);
        }
    };

    const channels = [
        {
            title: 'Email',
            body: 'Best for detailed project discussions, proposals, or technical questions.',
            action: { label: EMAIL, href: `mailto:${EMAIL}` },
            icon: 'M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
        },
        {
            title: 'LinkedIn',
            body: 'Quick questions, introductions, or anything that starts as a short back-and-forth.',
            action: { label: 'Message me', href: LINKEDIN, external: true },
            icon: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z M4 2a2 2 0 110 4 2 2 0 010-4z',
        },
        {
            title: 'Book a call',
            body: 'Grab a dedicated slot for an in-depth project conversation or consultation.',
            action: { label: 'See available times', onClick: () => setIsBookingOpen(true) },
            icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
        },
    ];

    return (
        <>
            {isBookingOpen && <BookingModal onClose={() => setIsBookingOpen(false)} />}

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-white/[0.06] bg-space-900 pt-36 pb-20 sm:pt-44 sm:pb-24">
                <div
                    className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[980px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-40 blur-[130px]"
                    style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.5), transparent 70%)' }}
                    aria-hidden="true"
                />
                <div className="relative mx-auto max-w-content px-6">
                    <div className="max-w-3xl reveal-element">
                        <div className="eyebrow">Contact</div>
                        <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-chalk sm:text-6xl">
                            What are you working on?
                        </h1>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-chalk-dim">
                            Looking for technical depth on an AI product, a second opinion on an
                            architecture, or someone to build the whole thing? Start here.
                        </p>
                    </div>
                </div>
            </section>

            {/* Channels */}
            <section className="bg-space-900 py-20 sm:py-24">
                <div className="mx-auto max-w-content px-6">
                    <div className="grid gap-6 md:grid-cols-3">
                        {channels.map((channel, i) => (
                            <div key={channel.title} data-reveal-index={i} className="card card--hover reveal-element flex flex-col p-8">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-accent/25 bg-accent/10">
                                    <svg className="h-5 w-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d={channel.icon} />
                                    </svg>
                                </div>
                                <h3 className="mt-5 font-display text-lg font-semibold text-chalk">{channel.title}</h3>
                                <p className="mt-3 flex-1 text-[15px] leading-relaxed text-chalk-dim">{channel.body}</p>
                                {channel.action.href ? (
                                    <a
                                        href={channel.action.href}
                                        target={channel.action.external ? '_blank' : undefined}
                                        rel={channel.action.external ? 'noreferrer' : undefined}
                                        className="mt-6 inline-flex break-all text-sm font-semibold text-[#b59aff] transition-colors hover:text-chalk"
                                    >
                                        {channel.action.label}
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={channel.action.onClick}
                                        className="mt-6 inline-flex self-start text-sm font-semibold text-[#b59aff] transition-colors hover:text-chalk"
                                    >
                                        {channel.action.label}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form */}
            <section className="border-y border-white/[0.06] bg-space-800 py-24 sm:py-32">
                <div className="mx-auto max-w-3xl px-6">
                    <div className="reveal-element">
                        <div className="eyebrow">Project inquiry</div>
                        <h2 className="mt-4 font-display text-3xl font-bold text-chalk sm:text-4xl">
                            Tell me about the work.
                        </h2>
                        <p className="mt-4 leading-relaxed text-chalk-dim">
                            The more context you give, the more useful my first reply will be.
                        </p>
                    </div>

                    <div className="card mt-10 p-7 sm:p-10 reveal-element">
                        <form className="space-y-7" onSubmit={handleSubmit}>
                            <fieldset>
                                <legend className="mb-3 text-sm font-semibold text-chalk">
                                    What kind of project is it?
                                </legend>
                                <div className="grid gap-3 sm:grid-cols-3">
                                    {projectTypes.map(([value, label, hint]) => (
                                        <label key={value} className="cursor-pointer">
                                            <input
                                                type="radio"
                                                name="projectType"
                                                value={value}
                                                className="sr-only"
                                                checked={projectType === value}
                                                onChange={() => setProjectType(value)}
                                            />
                                            <div
                                                className={`h-full rounded-xl border p-4 transition-all duration-250 ${
                                                    projectType === value
                                                        ? 'border-accent/70 bg-accent/10'
                                                        : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                                                }`}
                                            >
                                                <div className="font-semibold text-chalk">{label}</div>
                                                <div className="mt-1.5 text-xs leading-relaxed text-chalk-mute">{hint}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </fieldset>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="name" className="mb-2 block text-sm font-semibold text-chalk">
                                        Full name *
                                    </label>
                                    <input
                                        id="name" name="name" autoComplete="name" type="text" required
                                        value={form.name} onChange={update('name')}
                                        className="form-field w-full px-4 py-3"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="mb-2 block text-sm font-semibold text-chalk">
                                        Email address *
                                    </label>
                                    <input
                                        id="email" name="email" autoComplete="email" spellCheck={false} type="email" required
                                        value={form.email} onChange={update('email')}
                                        className="form-field w-full px-4 py-3"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="company" className="mb-2 block text-sm font-semibold text-chalk">
                                        Company / organization
                                    </label>
                                    <input
                                        id="company" name="company" autoComplete="organization" type="text"
                                        value={form.company} onChange={update('company')}
                                        className="form-field w-full px-4 py-3"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="role" className="mb-2 block text-sm font-semibold text-chalk">
                                        Your role
                                    </label>
                                    <input
                                        id="role" name="role" autoComplete="organization-title" type="text"
                                        value={form.role} onChange={update('role')}
                                        className="form-field w-full px-4 py-3"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="budget" className="mb-2 block text-sm font-semibold text-chalk">
                                        Budget range
                                    </label>
                                    <select
                                        id="budget" name="budget"
                                        value={form.budget} onChange={update('budget')}
                                        className="form-field w-full px-4 py-3"
                                    >
                                        {budgets.map(([value, label]) => (
                                            <option key={label} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="timeline" className="mb-2 block text-sm font-semibold text-chalk">
                                        Timeline
                                    </label>
                                    <select
                                        id="timeline" name="timeline"
                                        value={form.timeline} onChange={update('timeline')}
                                        className="form-field w-full px-4 py-3"
                                    >
                                        {timelines.map(([value, label]) => (
                                            <option key={label} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="message" className="mb-2 block text-sm font-semibold text-chalk">
                                    Project details *
                                </label>
                                <textarea
                                    id="message" name="message" rows="6" required
                                    value={form.message} onChange={update('message')}
                                    placeholder="What are you building, what’s in the way, and what does success look like?"
                                    className="form-field w-full resize-none px-4 py-3"
                                />
                            </div>

                            <div className="flex flex-col items-center gap-4">
                                <button
                                    type="submit"
                                    className="btn-primary w-full rounded-xl px-8 py-4 font-semibold sm:w-auto"
                                    disabled={isSending}
                                >
                                    {isSending ? 'Sending…' : 'Send message'}
                                    {!isSending && (
                                        <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                    )}
                                </button>

                                {isSent && (
                                    <div
                                        role="status"
                                        className="w-full rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-center"
                                    >
                                        <div className="font-semibold text-emerald-300">Message sent</div>
                                        <div className="mt-1 text-sm text-emerald-200/80">
                                            Thanks for reaching out. I&apos;ll reply to the email you provided.
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div
                                        role="alert"
                                        className="w-full rounded-xl border border-red-400/30 bg-red-400/10 p-4 text-center"
                                    >
                                        <div className="font-semibold text-red-300">Didn&apos;t send</div>
                                        <div className="mt-1 text-sm text-red-200/80">{error}</div>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            {/* Socials */}
            <section className="bg-space-900 py-20 sm:py-24">
                <div className="mx-auto max-w-content px-6 text-center">
                    <div className="reveal-element">
                        <div className="eyebrow">Elsewhere</div>
                        <h2 className="mt-4 font-display text-2xl font-bold text-chalk sm:text-3xl">
                            Follow the work.
                        </h2>
                    </div>
                    <div className="mt-8 flex justify-center gap-4 reveal-element">
                        {[
                            ['GitHub', GITHUB, 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z'],
                            ['LinkedIn', LINKEDIN, 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'],
                        ].map(([label, href, path]) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={label}
                                className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-chalk-dim transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:text-chalk"
                            >
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d={path} />
                                </svg>
                            </a>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
